import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardList, Check, XCircle, Loader2, Megaphone, Clock,
  Trash2, Play,
} from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: "Nháp", color: "bg-slate-500/20 text-slate-400" },
  pending_approval: { label: "Chờ duyệt", color: "bg-amber-500/20 text-amber-400" },
  approved: { label: "Đã duyệt", color: "bg-lime-500/20 text-lime-400" },
  executed: { label: "Đã thực hiện", color: "bg-emerald-500/20 text-emerald-400" },
  cancelled: { label: "Đã huỷ", color: "bg-red-500/20 text-red-400" },
};

export default function ActionDraftsPanel() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const fetchActions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (filter) params.set("status", filter);
      const { data } = await api.get(`/copilot/actions?${params}`);
      setActions(data.actions || []);
    } catch { toast.error("Lỗi tải actions"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/copilot/actions/${id}`, { status });
      toast.success(`Cập nhật → ${STATUS_CONFIG[status]?.label || status}`);
      fetchActions();
    } catch { toast.error("Lỗi"); }
  };

  const deleteAction = async (id) => {
    try { await api.delete(`/copilot/actions/${id}`); fetchActions(); }
    catch { toast.error("Lỗi xoá"); }
  };

  return (
    <div className="flex-1 overflow-y-auto" data-testid="actions-panel">
      <div className="sticky top-0 bg-[#0f172a] border-b border-[#1e293b] px-6 py-4 z-10">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div>
            <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-lime-400" /> Action Drafts
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Campaign & Task drafts từ AI Copilot — Duyệt hoặc huỷ</p>
          </div>
          <div className="flex gap-1">
            {["", "draft", "approved", "executed", "cancelled"].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-2 py-1 rounded text-[10px] transition-colors ${filter === s ? "bg-lime-500/20 text-lime-400" : "text-slate-500 hover:text-slate-300"}`}>
                {s ? STATUS_CONFIG[s]?.label : "Tất cả"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 max-w-4xl mx-auto space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-lime-400 animate-spin" />
          </div>
        ) : actions.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="w-10 h-10 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Chưa có action nào. Hỏi Copilot để tạo campaigns/tasks.</p>
          </div>
        ) : (
          actions.map(a => {
            const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.draft;
            return (
              <div key={a.id} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid={`action-${a.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[9px] border-0 ${a.type === "campaign" ? "bg-purple-500/20 text-purple-400" : "bg-sky-500/20 text-sky-400"}`}>
                      {a.type === "campaign" ? "Chiến dịch" : "Task"}
                    </Badge>
                    <h3 className="text-sm font-medium text-slate-200">{a.title}</h3>
                  </div>
                  <Badge className={`text-[9px] border-0 ${cfg.color}`}>{cfg.label}</Badge>
                </div>

                {/* Payload details */}
                {a.payload && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[10px] mb-3 bg-[#0f172a] rounded-lg p-2.5">
                    {a.payload.description && <div className="col-span-2 md:col-span-3"><span className="text-slate-600">Mô tả:</span> <span className="text-slate-400">{a.payload.description}</span></div>}
                    {a.payload.target_segment && <div><span className="text-slate-600">Đối tượng:</span> <span className="text-slate-300">{a.payload.target_segment}</span></div>}
                    {a.payload.offer && <div><span className="text-slate-600">Ưu đãi:</span> <span className="text-lime-400">{a.payload.offer}</span></div>}
                    {a.payload.channel && <div><span className="text-slate-600">Kênh:</span> <span className="text-sky-400">{a.payload.channel}</span></div>}
                    {a.payload.department && <div><span className="text-slate-600">Phòng ban:</span> <span className="text-slate-300">{a.payload.department}</span></div>}
                    {a.payload.priority && <div><span className="text-slate-600">Ưu tiên:</span> <span className="text-amber-400">{a.payload.priority}</span></div>}
                    {a.payload.deadline && <div><span className="text-slate-600">Deadline:</span> <span className="text-red-400">{a.payload.deadline}</span></div>}
                  </div>
                )}

                <div className="flex items-center gap-2 text-[10px] text-slate-600 mb-3">
                  <Clock className="w-3 h-3" /> {a.created_at?.split("T")[0]} • {a.created_by}
                  {a.reason_from_ai && <span className="text-slate-500 ml-2">— {a.reason_from_ai}</span>}
                </div>

                {/* Action buttons */}
                {a.status === "draft" && (
                  <div className="flex gap-2">
                    <Button onClick={() => updateStatus(a.id, "approved")} size="sm"
                      className="bg-lime-500/10 text-lime-400 hover:bg-lime-500/20 border border-lime-500/20 h-7 text-[10px] gap-1" data-testid={`approve-${a.id}`}>
                      <Check className="w-3 h-3" /> Duyệt
                    </Button>
                    <Button onClick={() => updateStatus(a.id, "executed")} size="sm"
                      className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 h-7 text-[10px] gap-1">
                      <Play className="w-3 h-3" /> Thực hiện
                    </Button>
                    <Button onClick={() => updateStatus(a.id, "cancelled")} size="sm"
                      className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 h-7 text-[10px] gap-1" data-testid={`cancel-${a.id}`}>
                      <XCircle className="w-3 h-3" /> Huỷ
                    </Button>
                    <button onClick={() => deleteAction(a.id)} className="ml-auto text-slate-600 hover:text-red-400 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {a.status === "approved" && (
                  <div className="flex gap-2">
                    <Button onClick={() => updateStatus(a.id, "executed")} size="sm"
                      className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 h-7 text-[10px] gap-1">
                      <Play className="w-3 h-3" /> Thực hiện
                    </Button>
                    <Button onClick={() => updateStatus(a.id, "cancelled")} size="sm"
                      className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 h-7 text-[10px] gap-1">
                      <XCircle className="w-3 h-3" /> Huỷ
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
